import { useState } from 'react';
import { Shield, Download, Trash2, ExternalLink } from 'lucide-react';
import { palette, admin, transition, fontFamily } from '../../../styles/tokens';
import { SettingsSubPage } from '../components/SettingsSubPage';
import { Toast } from '../../../components/common/Toast';
import { ConfirmDialog } from '../../../components/common/ConfirmDialog';

export function DataPrivacyPage() {
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const showToast = (message: string) => {
    setToast({ message, type: 'success' });
  };

  return (
    <SettingsSubPage title="Data & privacy" description="Export data, delete account, PIPEDA">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Export data */}
        <div style={{
          background: palette.white,
          borderRadius: admin.cardRadius,
          border: `1px solid ${admin.border}`,
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '14px 20px',
            borderBottom: `1px solid ${palette.g100}`,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}>
            <Download size={16} color={palette.g500} strokeWidth={1.8} />
            <span style={{ fontSize: 14, fontWeight: 600, color: palette.g800 }}>Export your data</span>
          </div>
          <div style={{ padding: '16px 20px' }}>
            <div style={{ fontSize: 13, color: palette.g500, lineHeight: 1.5, marginBottom: 16 }}>
              Download a CSV export of your business data including clients, bookings, invoices, and team records.
            </div>
            <button
              onClick={() => showToast('Data export coming in the next update.')}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '8px 16px', borderRadius: 8,
                border: `1px solid ${admin.border}`,
                background: palette.white, fontSize: 13, fontWeight: 500,
                color: palette.g600, cursor: 'pointer', fontFamily,
                transition: `border-color ${transition.fast}`,
              }}
            >
              <Download size={14} />
              Export all data (CSV)
            </button>
          </div>
        </div>

        {/* PIPEDA */}
        <div style={{
          background: palette.white,
          borderRadius: admin.cardRadius,
          border: `1px solid ${admin.border}`,
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '14px 20px',
            borderBottom: `1px solid ${palette.g100}`,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}>
            <Shield size={16} color={palette.g500} strokeWidth={1.8} />
            <span style={{ fontSize: 14, fontWeight: 600, color: palette.g800 }}>Privacy & compliance</span>
          </div>
          <div style={{ padding: '16px 20px' }}>
            <div style={{ fontSize: 13, color: palette.g500, lineHeight: 1.6 }}>
              CrewBit is committed to protecting your data under Canada's Personal Information Protection and Electronic Documents Act (PIPEDA).
              We only collect information necessary to provide our services, and you can request deletion at any time.
            </div>
            <a
              href="https://crewbit.ca/privacy"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                marginTop: 12, fontSize: 13, fontWeight: 500,
                color: palette.accent, textDecoration: 'none',
              }}
            >
              Privacy Policy
              <ExternalLink size={12} />
            </a>
          </div>
        </div>

        {/* Danger zone: delete */}
        <div style={{
          background: palette.white,
          borderRadius: admin.cardRadius,
          border: `1px solid ${admin.border}`,
          borderLeft: `3px solid ${palette.red}`,
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '14px 20px',
            borderBottom: `1px solid ${palette.g100}`,
          }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: palette.red }}>Danger zone</span>
          </div>
          <div style={{
            padding: '16px 20px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div>
              <div style={{ fontSize: 13.5, fontWeight: 500, color: palette.g800 }}>Delete your account</div>
              <div style={{ fontSize: 12, color: palette.g400, marginTop: 2 }}>
                Permanently removes all data. This cannot be undone.
              </div>
            </div>
            <button
              onClick={() => setConfirmDelete(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '7px 16px', borderRadius: 6,
                border: `1px solid ${palette.red}`,
                background: palette.white, fontSize: 13, fontWeight: 600,
                color: palette.red, cursor: 'pointer', fontFamily,
                transition: `all ${transition.fast}`,
              }}
            >
              <Trash2 size={14} />
              Delete account
            </button>
          </div>
        </div>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={!!toast}
          onDismiss={() => setToast(null)}
        />
      )}

      <ConfirmDialog
        isOpen={confirmDelete}
        title="Delete your account?"
        message="This action is permanent and cannot be undone. Your business data — clients, jobs, invoices, team — will be removed. Our support team guides you through the process and confirms before any data is removed."
        confirmLabel="Contact support"
        confirmVariant="danger"
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => {
          setConfirmDelete(false);
          showToast('Please contact support@crewbit.ca to request account deletion.');
        }}
      />
    </SettingsSubPage>
  );
}
